import { Amplify } from 'aws-amplify'
import {
  signIn,
  signUp,
  signOut,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  updatePassword
} from 'aws-amplify/auth'
import { getPreferences } from '@/modules/storage'

import { HOSTNAME, HOSTS, IS_DEV, IS_STAGE, LOCAL_ENV } from '@/modules/env'
import { getCookieDomain } from '@/modules/subdomainAuth'
import { featureFlags } from '@/modules/featureFlags'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { CookieStorage } from 'aws-amplify/utils'
import configDev from './config-dev'
import configStage from './config-stage'
import configProd from './config-prod'

let config
if (IS_DEV || HOSTNAME === HOSTS.LOCALHOST || HOSTNAME === HOSTS.LOOPBACK || HOSTNAME === HOSTS.LOCAL_TEST) {
  config = configDev
} else if (IS_STAGE) {
  config = configStage
} else {
  config = configProd
}

if (getPreferences('environment') !== null) {
  switch (getPreferences('environment')) {
    case LOCAL_ENV.STAGE:
      config = configStage
      break
    case LOCAL_ENV.DEV:
      config = configDev
      break
    default:
      config = configProd
      break
  }
}

let configured = false

const ensureConfigured = () => {
  if (!configured && config.userPoolId && config.clientId) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.clientId,
          loginWith: {
            email: true
          }
        }
      }
    })

    const cookieDomain = getCookieDomain()
    if (cookieDomain && featureFlags.hasStockAppNav) {
      cognitoUserPoolsTokenProvider.setKeyValueStorage(
        new CookieStorage({
          domain: cookieDomain,
          secure: true,
          path: '/',
          sameSite: 'strict',
          expires: 30
        })
      )
    }

    configured = true
  }
}

export const signInCognito = (email, password) => {
  ensureConfigured()
  return signIn({
    username: email,
    password,
    options: {
      authFlowType: 'USER_PASSWORD_AUTH'
    }
  })
}

export const signUpCognito = (email, password) => {
  ensureConfigured()
  return signUp({
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        preferred_username: email
      }
    }
  })
}

export const signOutCognito = () => {
  ensureConfigured()
  return signOut()
}

export const getCognitoUserToken = async () => {
  ensureConfigured()
  try {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()
    return idToken || null
  } catch {
    return null
  }
}

// Cognito usernames are the user's sub (a UUID), so read the email from the ID token instead
export const getCognitoUserEmail = async () => {
  ensureConfigured()
  try {
    const session = await fetchAuthSession()
    const email = session.tokens?.idToken?.payload?.email
    if (email) return String(email)
    const user = await getCurrentUser()
    return user?.signInDetails?.loginId || null
  } catch {
    return null
  }
}

export const getCurrentCognitoUser = async () => {
  ensureConfigured()
  try {
    const user = await getCurrentUser()
    return user
  } catch {
    return null
  }
}

export const forgotPasswordCognito = (email) => {
  ensureConfigured()
  return resetPassword({
    username: email
  })
}

export const confirmForgotPasswordCognito = (email, confirmationCode, newPassword) => {
  ensureConfigured()
  return confirmResetPassword({
    username: email,
    confirmationCode,
    newPassword
  })
}

export const updatePasswordCognito = (oldPassword, newPassword) => {
  ensureConfigured()
  return updatePassword({
    oldPassword,
    newPassword
  })
}

export const getCognitoConfig = () => config
